import React from "react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";
import { AntInput } from "@/CommonElements/AntInput";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions/SelectCachedOptions";
import { SelectPorts } from "@/Component/SelectPorts";
import { SelectFlightAirlines } from "@/Component/SelectFlightAirlines";
import { addQToItem } from "@/utils/helpers";
import { DATE_TIME_FORMAT_INPUT, TIME_FORMAT } from "@/utils/constants";

export const getFlightFormFields = (t, getLocalizedText, terminals) => [
  {
    name: "flight_no",
    labelKey: "FLIGHT_NUMBER",
    type: "input",
    required: true,
  },
  {
    name: "direction",
    labelKey: "TRAVEL_TYPE",
    type: "select",
    required: true,
    options: [
      { value: "A", label: t("A") },
      { value: "D", label: t("D") },
    ],
  },
  {
    name: "id",
    labelKey: "FLIGHT_CODE",
    type: "input",
    filterOnly: true,
  },
  {
    name: "flight_date",
    labelKey: "FLIGHT_DATE",
    type: "date",
    required: true,
  },
  {
    name: "flight_date_from",
    labelKey: "FLIGHT_DATE_FROM",
    type: "date",
    filterOnly: true,
  },
  {
    name: "flight_date_to",
    labelKey: "FLIGHT_DATE_TO",
    type: "date",
    filterOnly: true,
  },
  {
    name: "airline_id",
    labelKey: "AIRLINE",
    type: "selectFlightAirlines",
    createOnly: true,
  },
  {
    name: "terminal_code",
    labelKey: "TERMINAL",
    type: "select",
    optionFilterProp: "q",
    options: terminals.map(addQToItem).map(({ q, ...term }) => ({
      q,
      value: term.code,
      label: [term?.code, getLocalizedText(term?.name_ar, term?.name_en)].join(" - ") || term?.code,
    })),
  },
  {
    name: "airport_code",
    labelKey: "AIRLINE",
    render: ({ controllerField, label }) => (
      <SelectFlightAirlines
        {...controllerField}
        placeholder={label}
      />
    ),
  },
  {
    name: "from_city_id",
    labelKey: "DEPARTURE_CITY",
    type: "select",
    optionKey: "flight_cities",
    options: (city) => ({
      value: city.id,
      label: getLocalizedText(city?.name_ar, city?.name_en),
    }),
  },
  {
    name: "to_city_id",
    labelKey: "ARRIVAL_CITY",
    type: "select",
    optionKey: "flight_cities",
    options: (city) => ({
      value: city.id,
      label: getLocalizedText(city?.name_ar, city?.name_en),
    }),
  },
  {
    name: "max_capacity",
    labelKey: "SEATS_COUNT",
    type: "input",
    inputType: "number",
    createOnly: true,
  },
];

/** Map shared field config to GenericFilterModal filterFields shape */
export const toFilterFields = (fieldConfigs, t, getLocalizedText) =>
  fieldConfigs
    // Filters need a known "type". Fields with custom React renderers are for create only.
    .filter((f) => !f.createOnly && !!f.type)
    .map(({ filterOnly: _, createOnly: __, required: ___, labelKey, type, ...rest }) => {
      const base = { ...rest, label: t(labelKey) };
      if (type === "selectPorts") {
        return {
          ...base,
          type: "select",
          optionKey: "ports",
          options: (port) => ({
            value: port.id,
            label: getLocalizedText?.(port?.name_ar, port?.name_en) || port?.name || port?.code,
          }),
        };
      }
      return { ...base, type };
    });

/** Map one shared field config to a Controller entry for the create form */
export const fieldConfigToController = (field, { control, errors, t, getLocalizedText }) => {
  const { name, labelKey, type, required, optionKey, options, inputType, render } = field;
  const label = t(labelKey);
  const commonLabelProps = { children: label };
  const commonControllerProps = { name };

  if (required) {
    commonLabelProps.className = "required";
    commonControllerProps.rules = { required: t("FIELD_REQUIRED") };
  }

  if (type === "input") {
    return {
      name,
      labelProps: commonLabelProps,
      controllerProps: {
        ...commonControllerProps,
        render: ({ field: f, fieldState: { invalid } }) => (
          <AntInput
            {...f}
            type={inputType || "text"}
            min={inputType === "number" ? 0 : undefined}
            placeholder={label}
            status={invalid ? "error" : undefined}
          />
        ),
      },
    };
  }

  // Support fully custom controller renderers (field.render) for fields
  // that don't fit the common "type" mapping.
  if (typeof render === "function") {
    return {
      name,
      labelProps: commonLabelProps,
      controllerProps: {
        ...commonControllerProps,
        render: ({ field: f }) =>
          render({
            controllerField: f,
            label,
          }),
      },
    };
  }

  if (type === "date") {
    return {
      name,
      labelProps: commonLabelProps,
      controllerProps: {
        ...commonControllerProps,
        render: ({ field: f, fieldState: { invalid } }) => (
          <DatePicker
            size="large"
            value={f.value ? dayjs(f.value) : null}
            onChange={(date) => f.onChange(date)}
            className="w-100"
            placeholder={label}
            status={invalid ? "error" : undefined}
            format={DATE_TIME_FORMAT_INPUT}
            showTime={{ format: TIME_FORMAT, minuteStep: 1 }}
          />
        ),
      },
    };
  }

  if (type === "selectPorts") {
    return {
      name,
      labelProps: commonLabelProps,
      controllerProps: {
        ...commonControllerProps,
        render: ({ field: f, fieldState: { invalid } }) => (
          <SelectPorts
            {...f}
            placeholder={label}
            status={invalid ? "error" : undefined}
            style={{ width: "100%", minHeight: 40 }}
          />
        ),
      },
    };
  }

  if (type === "selectFlightAirlines") {
    return {
      name,
      labelProps: commonLabelProps,
      controllerProps: {
        ...commonControllerProps,
        render: ({ field: f, fieldState: { invalid } }) => (
          <SelectFlightAirlines
            {...f}
            placeholder={label}
            status={invalid ? "error" : undefined}
            style={{ width: "100%", minHeight: 40 }}
          />
        ),
      },
    };
  }

  if (type === "select") {
    if (optionKey) {
      return {
        name,
        labelProps: commonLabelProps,
        controllerProps: {
          ...commonControllerProps,
          render: ({ field: f, fieldState: { invalid } }) => (
            <SelectCachedOptions
              {...f}
              optionKey={optionKey}
              options={options}
              placeholder={label}
              status={invalid ? "error" : undefined}
              showSearch
            />
          ),
        },
      };
    }
    return {
      name,
      labelProps: commonLabelProps,
      controllerProps: {
        ...commonControllerProps,
        render: ({ field: f, fieldState: { invalid } }) => (
          <Select
            {...f}
            size="large"
            placeholder={label}
            status={invalid ? "error" : undefined}
            showSearch
            optionFilterProp="q"
            options={Array.isArray(options) ? options : []}
            style={{ width: "100%", minHeight: 40 }}
          />
        ),
      },
    };
  }

  return { name, labelProps: commonLabelProps, controllerProps: commonControllerProps };
};
